/**
 * Studio 系列：目录与任务关联由后端 `/api/studio/*` + `review_series` / `review_tasks` 承载；
 * 本文件保留「本机扫描记录」上的 seriesId 与本地列表辅助，供无 task_id 或离线列表使用。
 */
import type { StudioSeriesDto } from "../../services/compliance";
import type { StoredStudioScan } from "./studioScanHistoryStorage";
import {
  getStudioScanById,
  loadStudioScanHistory,
  setScanSeriesId,
  sortStudioScansByTime,
} from "./studioScanHistoryStorage";
import { patchReviewTaskSeries } from "../../services/compliance";

/** UI 用：与接口 `title` 对应为 `name` */
export type StudioSeries = {
  id: string;
  name: string;
  createdAt: number;
};

export function studioSeriesDtoToUi(d: StudioSeriesDto): StudioSeries {
  return {
    id: d.id,
    name: d.title,
    createdAt: new Date(d.created_at).getTime(),
  };
}

/** 同系列的本机扫描记录，完成时间新→旧 */
export function listScansInSeries(seriesId: string): StoredStudioScan[] {
  return sortStudioScansByTime(
    loadStudioScanHistory().filter((r) => r.seriesId === seriesId),
  );
}

export function getSeriesIdForScan(scanDisplayId: string): string | undefined {
  return getStudioScanById(scanDisplayId)?.seriesId;
}

/** 同系列内除当前外的本机版本，新→旧 */
export function getComparableVersions(scanDisplayId: string): StoredStudioScan[] {
  const sid = getSeriesIdForScan(scanDisplayId);
  if (!sid) return [];
  return listScansInSeries(sid).filter((r) => r.scanDisplayId !== scanDisplayId);
}

/** 仅本机记录的时间线（旧→新）；有后端时请优先用 fetchStudioSeriesTasks */
export function getLocalSeriesTimeline(scanDisplayId: string): StoredStudioScan[] {
  const row = getStudioScanById(scanDisplayId);
  if (!row) return [];
  if (!row.seriesId) return [row];
  return [...listScansInSeries(row.seriesId)].sort(
    (a, b) => a.completedAt - b.completedAt,
  );
}

/**
 * 更新本机记录的 seriesId，并在有 task_id 时同步 PATCH 后端。
 */
export async function assignScanToSeries(
  scanDisplayId: string,
  seriesId: string | null,
): Promise<boolean> {
  const row = getStudioScanById(scanDisplayId);
  if (row?.taskId) {
    try {
      await patchReviewTaskSeries(row.taskId, seriesId);
    } catch {
      return false;
    }
  }
  return setScanSeriesId(scanDisplayId, seriesId ?? undefined);
}
